import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/biens", label: "Nos biens" },
  { to: "/vendre", label: "Vendre" },
  { to: "/acheter", label: "Acheter" },
  { to: "/programmes-neufs", label: "Neuf" },
  { to: "/agence", label: "Notre agence" },
  { to: "/conseils", label: "Conseils" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b border-gold/15 bg-[#0A0A0A]/95 backdrop-blur-xl transition-all duration-500 ${
        scrolled ? "shadow-[0_10px_40px_rgba(0,0,0,0.45)]" : ""
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8 lg:px-16">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
          <img
            src="/kemelia-logo-square.png"
            alt="Kemelia Estate"
            className="h-14 w-14 rounded-xl border border-gold/40 object-cover shadow-[0_2px_14px_rgba(212,175,55,0.3)] transition-transform duration-300 group-hover:scale-105"
          />
          <span className="flex flex-col leading-none">
          <span className="font-serif text-xl font-semibold tracking-[0.28em] text-white transition-colors duration-500">
            KEMELIA
          </span>
          <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.42em] text-gold transition-colors duration-500">
            Estate · Côte d'Azur
          </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `relative whitespace-nowrap font-serif text-sm font-medium uppercase tracking-[0.12em] transition-colors duration-300 text-white/80 hover:text-gold ${
                  isActive ? "after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-full after:bg-gold" : ""
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/vendre"
            data-testid="nav-cta-estimation"
            className="group inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-gold px-5 py-2.5 text-sm font-medium text-[#0A0A0A] transition-all duration-300 hover:bg-sand"
          >
            Estimer mon bien
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="text-white lg:hidden"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-gold/15 bg-[#0A0A0A] lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 pb-6 pt-2">
              {LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} className="border-b border-white/10 py-3 font-serif text-xl text-sand">
                  {l.label}
                </NavLink>
              ))}
              <Link to="/vendre" data-testid="nav-mobile-cta" className="mt-4 rounded-full bg-gold px-5 py-3 text-center text-sm font-semibold text-[#0A0A0A]">
                Estimer mon bien
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
